import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  beginTotpSetup,
  CognitoChallenge,
  CognitoError,
  CognitoStep,
  completeTotpSetup,
  confirmPasswordReset,
  initiateAuth,
  requestPasswordReset,
  respondToChallenge,
  storeSession,
  unusablePassword,
} from './cognitoAuth';
import {
  finishMicrosoftSignIn,
  isMicrosoftCallback,
  startMicrosoftSignIn,
} from './microsoftSignIn';
import { lambdaReason, SignInError, SignInFailure } from './signInFailure';
import { loginUrl, signInReturnPath } from './signInRedirect';

/** What the app's own sign-in page offers besides email and password. */
export interface SignInOptions {
  /** Entra app (client) id: offers Sign in with Microsoft. */
  microsoftClientId?: string;
  /** Offers Sign in with Google (the user pool's `Google` identity provider). */
  google?: boolean;
  /**
   * The page's background, matching the user pool's managed login page:
   * `photo` (the default, main's branding) or `shapes` (Cognito's default
   * background, which super-admin's pool keeps).
   */
  backdrop?: SignInBackdrop;
}

export type SignInBackdrop = 'photo' | 'shapes';

export type MfaMethod = 'SOFTWARE_TOKEN_MFA' | 'SMS_MFA' | 'EMAIL_OTP';

/** The step the page is on. */
export type SignInView =
  | { step: 'credentials' }
  | { step: 'completing' }
  | { step: 'new-password' }
  | { step: 'mfa-code'; method: MfaMethod; destination?: string }
  | { step: 'mfa-choice'; methods: MfaMethod[] }
  | { step: 'mfa-setup'; secret: string; account: string }
  | { step: 'forgot' }
  | { step: 'reset'; email: string; destination?: string };

export type SignInBusy = 'password' | 'microsoft' | 'google' | 'step' | null;

export interface SignInMessage {
  reason: SignInFailure | 'password_reset_done';
  /** Cognito's own wording, where it says something the reason can't (password rules). */
  detail?: string;
}

const MFA_ANSWER: Record<MfaMethod, string> = {
  SOFTWARE_TOKEN_MFA: 'SOFTWARE_TOKEN_MFA_CODE',
  SMS_MFA: 'SMS_MFA_CODE',
  EMAIL_OTP: 'EMAIL_OTP_CODE',
};

const isMfaMethod = (name: string): name is MfaMethod => name in MFA_ANSWER;

/** Cognito lists MFA types as a JSON array string. */
const mfaList = (value = '[]'): MfaMethod[] =>
  (JSON.parse(value) as string[]).filter(isMfaMethod);

interface Attempt {
  flow: 'password' | 'microsoft';
  email: string;
  /** The Microsoft ID token, answered automatically to the custom challenge. */
  idToken?: string;
  returnTo: string;
}

const cognitoFailure = (
  error: CognitoError,
  flow: Attempt['flow']
): SignInFailure => {
  const fromLambda = lambdaReason(error.message);
  if (fromLambda) return fromLambda;

  switch (error.type) {
    case 'InvalidParameterException':
      // e.g. "CUSTOM_AUTH is not enabled for the client", or no custom-auth trigger.
      return /not enabled|not configured|trigger/i.test(error.message)
        ? 'sign_in_unavailable'
        : 'sign_in_failed';
    case 'UserLambdaValidationException':
      // The trigger failed without giving a reason: it is broken or misconfigured.
      return 'sign_in_unavailable';
    case 'NotAuthorizedException':
      if (/disabled/i.test(error.message)) return 'account_disabled';
      if (/attempts exceeded/i.test(error.message)) return 'too_many_attempts';
      if (/session/i.test(error.message)) return 'session_expired';
      // With user-existence errors suppressed, an unknown user also lands here.
      return flow === 'microsoft' ? 'account_not_found' : 'wrong_credentials';
    case 'UserNotFoundException':
      return flow === 'microsoft' ? 'account_not_found' : 'wrong_credentials';
    case 'CodeMismatchException':
    case 'ExpiredCodeException':
    case 'EnableSoftwareTokenMFAException':
      return 'invalid_code';
    case 'InvalidPasswordException':
      return 'invalid_password';
    case 'PasswordResetRequiredException':
      return 'password_reset_required';
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
    case 'LimitExceededException':
      return 'too_many_attempts';
    default:
      return 'sign_in_failed';
  }
};

const toMessage = (error: unknown, flow: Attempt['flow']): SignInMessage => {
  if (error instanceof SignInError) return { reason: error.reason };
  if (error instanceof CognitoError) {
    // The page only names the reason; this is what an admin needs to fix it.
    console.error(
      `Cognito refused the sign-in (${error.type}): ${error.message}`
    );
    const reason = cognitoFailure(error, flow);
    return reason === 'invalid_password'
      ? { reason, detail: error.message }
      : { reason };
  }
  console.error('Sign-in failed:', error);
  return { reason: 'sign_in_failed' };
};

/**
 * Drives the app's own sign-in page: email and password (with Cognito's
 * new-password, MFA and MFA-setup challenges, and password reset), Microsoft,
 * and Google. Every route ends in ordinary Cognito tokens, stored where the
 * AuthProvider looks for them, followed by a reload.
 */
// When the page last retried a Google sign-in after linking (see useSignIn).
const GOOGLE_RETRY_KEY = 'signIn.googleLinkRetry';
const GOOGLE_RETRY_WINDOW_MS = 2 * 60 * 1000;

/** Claims the one automatic retry; false if one already ran just now (or storage is unavailable). */
const claimGoogleRetry = (): boolean => {
  try {
    const last = Number(sessionStorage.getItem(GOOGLE_RETRY_KEY));
    if (last && Date.now() - last < GOOGLE_RETRY_WINDOW_MS) return false;
    sessionStorage.setItem(GOOGLE_RETRY_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
};

/**
 * @param failure A refusal reported before this page loaded, such as the
 *   Lambda's verdict on a Google sign-in that came back through Cognito.
 */
export const useSignIn = (options: SignInOptions, failure?: SignInFailure) => {
  const auth = useAuth();
  const settings = auth.settings;
  const { microsoftClientId } = options;

  const [view, setView] = useState<SignInView>(() =>
    microsoftClientId && isMicrosoftCallback()
      ? { step: 'completing' }
      : { step: 'credentials' }
  );
  const [busy, setBusy] = useState<SignInBusy>(null);
  const [message, setMessage] = useState<SignInMessage | null>(null);
  const attempt = useRef<Attempt>({
    flow: 'password',
    email: '',
    returnTo: '/',
  });
  const challenge = useRef<CognitoChallenge | null>(null);

  /** Follows Cognito to the next step; true once the session is stored and the page is reloading. */
  const advance = useCallback(
    async (step: CognitoStep): Promise<boolean> => {
      if ('tokens' in step) {
        await storeSession(settings, step.tokens);
        window.location.replace(attempt.current.returnTo);
        return true;
      }

      const next = step.challenge;
      challenge.current = next;
      const { flow, idToken, email } = attempt.current;

      if (next.name === 'CUSTOM_CHALLENGE' && idToken) {
        return advance(
          await respondToChallenge(settings, next, { ANSWER: idToken })
        );
      }
      if (next.name === 'NEW_PASSWORD_REQUIRED') {
        if (flow === 'password') {
          setView({ step: 'new-password' });
          return false;
        }
        // Invited, never set a password, and Microsoft has just proven who
        // they are: give them one nobody knows ("Forgot password" still works).
        return advance(
          await respondToChallenge(settings, next, {
            NEW_PASSWORD: unusablePassword(),
          })
        );
      }
      if (isMfaMethod(next.name)) {
        setView({
          step: 'mfa-code',
          method: next.name,
          destination: next.parameters.CODE_DELIVERY_DESTINATION,
        });
        return false;
      }
      if (next.name === 'SELECT_MFA_TYPE') {
        setView({
          step: 'mfa-choice',
          methods: mfaList(next.parameters.MFAS_CAN_CHOOSE),
        });
        return false;
      }
      if (next.name === 'MFA_SETUP') {
        if (
          !mfaList(next.parameters.MFAS_CAN_SETUP).includes(
            'SOFTWARE_TOKEN_MFA'
          )
        ) {
          throw new SignInError('mfa_setup_unavailable');
        }
        const setup = await beginTotpSetup(settings, next);
        challenge.current = setup.challenge;
        setView({ step: 'mfa-setup', secret: setup.secret, account: email });
        return false;
      }
      throw new SignInError('sign_in_failed');
    },
    [settings]
  );

  /** Runs one request with its button busy, and turns a refusal into a message. */
  const run = useCallback(
    async (kind: SignInBusy, task: () => Promise<boolean | void>) => {
      setBusy(kind);
      setMessage(null);
      try {
        if (await task()) return; // reloading: keep the button busy
      } catch (error) {
        setMessage(toMessage(error, attempt.current.flow));
      }
      setBusy(null);
    },
    []
  );

  const answer = (answers: Record<string, string>, name?: string) =>
    run('step', async () => {
      if (!challenge.current) throw new SignInError('session_expired');
      return advance(
        await respondToChallenge(settings, challenge.current, answers, name)
      );
    });

  /**
   * Starts the custom-auth sign-in for the first of the Microsoft account's
   * addresses that has a Consolidocs account: an organisation may have
   * invited the user under their email or under their sign-in name.
   */
  const initiateForFirstAccount = useCallback(
    async (addresses: string[]) => {
      for (const [index, address] of addresses.entries()) {
        attempt.current.email = address;
        try {
          return await initiateAuth(settings, 'CUSTOM_AUTH', address);
        } catch (error) {
          const tryNext =
            index < addresses.length - 1 &&
            error instanceof CognitoError &&
            cognitoFailure(error, 'microsoft') === 'account_not_found';
          if (!tryNext) throw error;
        }
      }
      throw new SignInError('account_not_found');
    },
    [settings]
  );

  // Finish a Microsoft sign-in the browser has just been sent back from.
  // Once only, even under StrictMode: Microsoft's code is single-use.
  const handledCallback = useRef(false);
  useEffect(() => {
    if (!microsoftClientId || handledCallback.current) return;
    if (!isMicrosoftCallback()) return;
    handledCallback.current = true;

    void run('step', async () => {
      try {
        const { addresses, idToken, returnTo } = await finishMicrosoftSignIn(
          microsoftClientId
        );
        attempt.current = {
          flow: 'microsoft',
          email: addresses[0],
          idToken,
          returnTo,
        };
        // Leave the callback URL so a reload can't replay the used code, but
        // stay on the sign-in page until Cognito has finished (it may want MFA).
        window.history.replaceState(null, '', loginUrl(returnTo));
        return await advance(await initiateForFirstAccount(addresses));
      } catch (error) {
        attempt.current.flow = 'microsoft';
        if (isMicrosoftCallback()) {
          const returnTo = error instanceof SignInError ? error.returnTo : '';
          window.history.replaceState(null, '', loginUrl(returnTo || '/'));
        }
        setView({ step: 'credentials' });
        throw error;
      }
    });
  }, [microsoftClientId, advance, run, initiateForFirstAccount]);

  const startGoogle = useCallback(
    () =>
      run('google', async () => {
        // Straight to Google through the pool's identity provider, skipping
        // Cognito's own login page.
        // The app's onSigninCallback reads returnTo back off the user.
        await auth.signinRedirect({
          state: { returnTo: signInReturnPath() },
          extraQueryParams: { identity_provider: 'Google' },
        });
        return true;
      }),
    [run, auth]
  );

  // The Pre sign-up check links a first Google login to its invited account
  // and lets that sign-in go on. Should Cognito still stop that one attempt,
  // the next goes straight in, so retry once, quietly. The guard outlives the
  // page load, so if anything else is wrong the user sees the message instead
  // of a redirect loop.
  useEffect(() => {
    if (failure !== 'account_linked' || !options.google) return;
    if (claimGoogleRetry()) void startGoogle();
  }, [failure, options.google, startGoogle]);

  // Coming back with the Back button restores this page from the bfcache
  // mid-redirect; give the user their buttons back.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setBusy(null);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const backToSignIn = () => {
    challenge.current = null;
    setMessage(null);
    setView({ step: 'credentials' });
  };

  return {
    view,
    busy,
    message,

    signInWithPassword: (email: string, password: string) =>
      run('password', async () => {
        attempt.current = {
          flow: 'password',
          email,
          returnTo: signInReturnPath(),
        };
        return advance(
          await initiateAuth(settings, 'USER_PASSWORD_AUTH', email, {
            PASSWORD: password,
          })
        );
      }),

    startMicrosoft: () =>
      run('microsoft', async () => {
        if (!microsoftClientId) return;
        await startMicrosoftSignIn(microsoftClientId);
        return true; // navigating to Microsoft
      }),

    startGoogle,

    submitNewPassword: (password: string) => answer({ NEW_PASSWORD: password }),

    submitMfaCode: (code: string) =>
      answer(
        view.step === 'mfa-code' ? { [MFA_ANSWER[view.method]]: code } : {}
      ),

    chooseMfa: (method: MfaMethod) => answer({ ANSWER: method }),

    confirmMfaSetup: (code: string) =>
      run('step', async () => {
        if (!challenge.current) throw new SignInError('session_expired');
        return advance(
          await completeTotpSetup(settings, challenge.current, code)
        );
      }),

    forgotPassword: () => {
      setMessage(null);
      setView({ step: 'forgot' });
    },

    sendResetCode: (email: string) =>
      run('step', async () => {
        const destination = await requestPasswordReset(settings, email);
        setView({ step: 'reset', email, destination });
      }),

    resetPassword: (code: string, password: string) =>
      run('step', async () => {
        if (view.step !== 'reset') return;
        await confirmPasswordReset(settings, view.email, code, password);
        setView({ step: 'credentials' });
        setMessage({ reason: 'password_reset_done' });
      }),

    backToSignIn,
  };
};

export type SignIn = ReturnType<typeof useSignIn>;
